import type { RouteLocationNormalizedGeneric, RouteLocationRaw } from 'vue-router'

import { isDashboardRoute, resolveFirstDashboard } from './firstDashboard'
import { HOME_ROUTE_NAME, MCP_ENABLE_ROUTE_NAME, MCP_HOME_ROUTE_NAME } from './routeNames'

import { freePlanLockedRoute } from '@/features/account'

export interface PermissionAccessSnapshot {
  isSysAdmin: boolean
  isAccountHolder?: boolean
  workspaceCount?: number
  onlyDashboard: boolean
  /**
   * The account's own `is_mcp_only` bit, as published by bootstrap (ORP-21656 AC 2). Optional and
   * absent-means-web: a snapshot that does not carry it is NOT evidence of an MCP-only account, so
   * every existing web decision stays byte-for-byte identical.
   */
  isMcpOnly?: boolean
  /**
   * The ADMIN-BYPASS persona (`rbac.can_access_admin_panel`, ORP-21835 item 4) — the backend's
   * `is_admin` flag OR the sysadmin identity, i.e. what its own admin-panel gate admits. Optional
   * for the same reason `isMcpOnly` is, but the absent case means the OPPOSITE thing: this bit
   * grants an exemption, so a snapshot that does not carry it is not evidence of one.
   */
  canAccessAdminPanel?: boolean
  /**
   * The account is on the Free plan package (ORP-26799). Optional and absent-means-paid: like
   * `isMcpOnly`, a snapshot that does not carry it is not evidence of anything.
   */
  isFreePlan?: boolean
  hasPermission: (key: string) => boolean
}

/**
 * ORP-26799: the Free sidebar lists every locked screen, and the click must land on the locked
 * screen rather than bounce home because the paid permission behind it is absent. Safe to admit:
 * `FreePlanRouteGate` renders in place of the page, so the locked page itself never mounts.
 */
function isFreePlanLockedTarget(
  to: Pick<RouteLocationNormalizedGeneric, 'name'>,
  rbac: PermissionAccessSnapshot,
): boolean {
  return rbac.isFreePlan === true && freePlanLockedRoute(to.name) !== null
}

/** One pure RBAC decision shared by navigation and post-error bootstrap retry revalidation. */
export function resolvePermissionAccess(
  to: Pick<RouteLocationNormalizedGeneric, 'name' | 'meta'>,
  rbac: PermissionAccessSnapshot,
): true | RouteLocationRaw {
  if (rbac.onlyDashboard && !isDashboardRoute(to)) {
    const target = resolveFirstDashboard()
    if (target.name !== to.name) return target
  }
  // No onboarding redirect: onboarding is a takeover over the app (contract 29 §11, amended
  // 2026-10-22). `OnboardingHost` opens it for a zero-workspace account wherever that account
  // lands and keeps it up until the run finishes, so the guard has nothing left to route.
  // The MCP-only surface split (ORP-21656 AC 1/3). The bit is BACKEND-decided — no package-id
  // mapping here — so buying a web package flips it to false and the web screens open with no
  // further work. Ordered AFTER onboarding because a zero-workspace MCP-only user must still
  // onboard first, and BEFORE the RBAC gates because "you do not own this product" outranks
  // "you lack this permission".
  //
  // Sysadmin is exempt: support has to be able to open a web screen while impersonating an
  // MCP-only account (AC 9's one defined outcome for the MCP × sysadmin combination). So is the
  // ADMIN-BYPASS persona (`can_access_admin_panel`, ORP-21835 item 4 — product decision): an
  // agency/admin user must be able to open an MCP-born CUSTOMER account on the web, and the
  // backend already treats the two the same everywhere else (`common/admin_bypass.py`:
  // `is_admin_bypass` = the `is_admin` flag OR `is_sysadmin_user`). The two are still checked
  // separately here because only the first is a required wire field: `can_access_admin_panel` is
  // optional, so it is compared `!== true` — an older backend or a degraded `rbac` section must
  // not exempt anyone by omission.
  //
  // Exemption is read from the DECLARED `meta.mcpAllowed`, never guessed from the path: an
  // unflagged OAuth return would bounce the user out of a connection they were completing.
  //
  // `!onlyDashboard` mirrors the branch above, and for the same reason it exists there. A
  // dashboard-only user is pulled onto a dashboard by the FIRST branch, which no MCP route can
  // ever satisfy — so without this the two branches would bounce a dashboard-only MCP account
  // between them until vue-router aborted the navigation. The combination is unreachable in
  // practice (`flowToolkit.computeOnlyDashboard` needs shared-dashboard access, which the MCP
  // product does not grant), and if it ever occurs the dashboard the user demonstrably has
  // access to is a better answer than a redirect loop.
  if (
    rbac.isMcpOnly === true &&
    !rbac.isSysAdmin &&
    rbac.canAccessAdminPanel !== true &&
    !rbac.onlyDashboard &&
    to.meta.mcpAllowed !== true
  ) {
    return { name: MCP_HOME_ROUTE_NAME }
  }
  // The other direction (ORP-21836): an account that does NOT hold the MCP product has no business
  // inside the MCP product's shell. Its data was never at risk — the backend closes the
  // entitlement chain (origin → plan → policy → role) on every call — so this is product-surface
  // consistency, not a security fix.
  //
  // `!== true` reads the SAME bit the SAME way the branch above does, rather than inverting it, so
  // exactly one of the two can ever fire and they cannot bounce against each other: an absent bit
  // is a web account on both sides.
  //
  // Sysadmin and the admin-bypass persona are exempt for the mirror of the reason they are exempt
  // above — support and agency admins have to be able to open the other product's screens.
  //
  // Ordered after onboarding for the same reason the branch above is: a zero-workspace account
  // still onboards first (AC 3). The dashboard-only branch already returned by here, so an
  // MCP deep link from such an account lands on its dashboard, never here.
  //
  // The target is the enable CARD, not home: the user asked for the MCP product, and a silent
  // bounce to Summary answers nothing. That record declares no `requiresMcp`, so this branch
  // cannot fire on its own destination.
  if (
    to.meta.requiresMcp === true &&
    rbac.isMcpOnly !== true &&
    !rbac.isSysAdmin &&
    rbac.canAccessAdminPanel !== true
  ) {
    return { name: MCP_ENABLE_ROUTE_NAME }
  }
  if (to.meta.requiresSysAdmin && !rbac.isSysAdmin) return { name: HOME_ROUTE_NAME }
  if (
    to.meta.requiresAccountCenterAccess &&
    ((rbac.workspaceCount ?? 0) < 2 || (!rbac.isAccountHolder && !rbac.isSysAdmin))
  ) {
    return { name: HOME_ROUTE_NAME }
  }
  // Last, after the sysadmin/account-center gates, which no plan upgrade would open either.
  return isPermissionDenied(to, rbac) ? { name: HOME_ROUTE_NAME } : true
}

function isPermissionDenied(
  to: Pick<RouteLocationNormalizedGeneric, 'name' | 'meta'>,
  rbac: PermissionAccessSnapshot,
): boolean {
  if (isFreePlanLockedTarget(to, rbac)) return false
  const key = to.meta.permission
  return key === 'sysadmin' ? !rbac.isSysAdmin : Boolean(key && !rbac.hasPermission(key))
}
